import classnames from 'classnames';
import React, { useContext } from 'react';
import styled, { ThemeContext, ThemeProvider } from 'styled-components';
import { BrandfolderHexColors } from '../../../../enums/colors.js';
import { FontSizes, FontWeights } from '../../../../enums/fonts.js';
import '../../../../styles/styled-components/alert.js';
import '../../../../styles/styled-components/animations.js';
import '../../../../styles/styled-components/box-shadow.js';
import '../../../../styles/styled-components/button.js';
import { getFontSize, getFontWeight } from '../../../../styles/styled-components/font.js';
import '../../../../styles/styled-components/font-face.js';
import '../../../../styles/styled-components/global.js';
import '../../../../themes/alert.js';
import '../../../../themes/button.js';
import '../../../../themes/checkbox.js';
import '../../../../themes/divider.js';
import '../../../../themes/dropdown.js';
import '../../../../themes/fieldset.js';
import '../../../../themes/input.js';
import '../../../../themes/list.js';
import '../../../../themes/listbox.js';
import '../../../../themes/loader.js';
import '../../../../themes/option.js';
import '../../../../themes/popout.js';
import '../../../../themes/switch.js';
import { lightTheme } from '../../../../themes/themes.js';
import '../../../../themes/tooltip.js';
import '../../../../styles/styled-components/input.js';
import '../../../../styles/styled-components/list.js';
import '../../../../styles/styled-components/listitem.js';
import '../../../../styles/styled-components/media-queries.js';
import '../../../../styles/styled-components/reset.js';
import '../../../../styles/styled-components/outline.js';
import '../../../../styles/styled-components/popout.js';
import { screenReaderOnly } from '../../../../styles/styled-components/screen-reader.js';
import '../../../../styles/styled-components/switch.js';
import '../../../../styles/styled-components/tooltip.js';
import { BaseLabel } from '../base-label/base-label.js';

const BaseStandardLabel = (props)=>{
    const { children, className, required, requiredLabel, requiredProps, showLabel, size, weight, ...otherProps } = props;
    const isRequired = required && requiredLabel;
    return /*#__PURE__*/ React.createElement(BaseLabel, {
        ...otherProps,
        className: classnames({
            'bf-label__standard': true,
            'bf-label__standard--hidden': !showLabel,
            'bf-label__standard--required': isRequired,
            [`bf-label__standard--size-${size}`]: !!size,
            [`bf-label__standard--weight-${weight}`]: !!weight,
            [`${className}`]: !!className
        })
    }, children, isRequired && /*#__PURE__*/ React.createElement("span", {
        ...requiredProps,
        "aria-label": requiredLabel,
        className: classnames({
            'bf-label__standard-required': true,
            [`${className}`]: !!className
        }),
        role: "img"
    }, "*"));
};
const Label = styled(BaseStandardLabel)`
  box-sizing: border-box;
  color: ${({ theme })=>theme.darkMode ? BrandfolderHexColors.White : BrandfolderHexColors.Text};
  display: flex;
  ${({ size })=>getFontSize({
        size
    })}
  ${({ weight })=>getFontWeight({
        weight
    })}
  ${({ showLabel })=>showLabel ? '' : screenReaderOnly}

  .bf-label__standard-required {
    color: ${BrandfolderHexColors.WarningRed};
  }
`;
/**
 * A `<label>` for an element like `<input>` or `<textarea>`.
 * Proper usage includes wrapping form inputs inside of the `<label>`, or
 * linking them with the `htmlFor` attribute to the element `id` attribute.
 * All text needs to be translated by your app.
 *
 * The `children` of the `<label>` should be basic [phrasing content](https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Content_categories#phrasing_content).
 * A `<label>` should not contain any `<a>` tags, `<h1>` through `<h6>` tags,
 * or `<button>` tags. A `<label>` should also not contain another nested `<label>`.
 *
 * Setting `required` to `true` will show an asterisk denoting that the corresponding form control is required.
 * Additionally, `aria-required="true"` needs to be set on the form control.
 * Optionally, you may also add instructions to your form, such as
 * `<StandardText>Fields marked with an asterisk (*) are required.</StandardText>`
 *
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label
 *
 * https://dequeuniversity.com/tips/identify-required-elements
 *
 * @param props StandardLabelProps
 * @returns FunctionComponent<StandardLabelProps>
 */ const StandardLabel = (props)=>{
    const { showLabel = true, size = FontSizes.Small, weight = FontWeights.Medium, ...otherProps } = props;
    const themeContext = useContext(ThemeContext);
    return /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: themeContext || lightTheme
    }, /*#__PURE__*/ React.createElement(Label, {
        ...otherProps,
        showLabel: showLabel,
        size: size,
        weight: weight
    }));
};

export { BaseStandardLabel, StandardLabel };
//# sourceMappingURL=standard-label.js.map
